import React from "react";
import { ArrowRight, Play } from "lucide-react";
import { Button } from "../ui/button";
import { SITE_CONFIG, STATS } from "../../data/mock";

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center hero-gradient overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[var(--accent-primary)] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[var(--accent-strong)] rounded-full mix-blend-multiply filter blur-3xl opacity-15" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-32 pb-20">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent-wash)] border border-[var(--accent-primary)]/20 mb-8">
            <span className="w-2 h-2 rounded-full bg-[var(--accent-strong)] animate-pulse" />
            <span className="text-sm font-medium text-[var(--accent-text)]">
              Trusted by 500+ Kenyan Schools
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[var(--text-primary)] leading-[0.95] tracking-tight mb-6">
            Simplify School
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--accent-strong)] to-[var(--accent-text)]">
              Administration
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-[var(--text-body)] max-w-2xl mx-auto mb-10 leading-relaxed opacity-90">
            {SITE_CONFIG.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a
              href={SITE_CONFIG.signupFormUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="btn-gradient text-white hover:opacity-90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-[var(--accent-primary)]/25 hover:shadow-xl hover:shadow-[var(--accent-primary)]/30 transition-all duration-300 hover:scale-[1.02] group"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 py-6 text-lg font-semibold border-[var(--border-medium)] text-[var(--text-primary)] hover:bg-[var(--bg-section)] transition-all duration-300 group"
            >
              <Play className="w-5 h-5 mr-2 text-[var(--accent-strong)]" />
              Watch Demo
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {STATS.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-[var(--text-secondary)] font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard Preview - Scaled down to look like a screenshot */}
        <div className="mt-20 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-page)] via-transparent to-transparent z-10 pointer-events-none" />
          <div className="relative rounded-2xl border border-[var(--border-light)] shadow-2xl overflow-hidden bg-[var(--bg-card)]">
            {/* Browser Chrome */}
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[var(--border-light)] bg-[var(--bg-section)]">
              <div className="w-2 h-2 rounded-full bg-red-400" />
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <span className="ml-3 text-[10px] text-[var(--text-muted)]">
                app.sefil360.com
              </span>
            </div>

            {/* Dashboard Layout - Scaled container */}
            <div className="overflow-hidden" style={{ height: "185px" }}>
              <div
                className="origin-top-left"
                style={{ transform: "scale(0.52)", width: "192.3%" }}
              >
                <div className="flex">
                  {/* Sidebar */}
                  <div className="w-40 border-r border-[var(--border-light)] bg-[var(--bg-card)] p-2 flex-shrink-0">
                    <div className="flex items-center gap-1.5 mb-3">
                      <div className="w-5 h-5 rounded bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] flex items-center justify-center">
                        <span className="text-white font-bold text-[8px]">
                          S
                        </span>
                      </div>
                      <span className="font-bold text-[10px] text-[var(--text-primary)]">
                        SEFIL 360
                      </span>
                    </div>
                    <nav className="space-y-0.5 text-[9px]">
                      <a
                        href="#"
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-[var(--accent-wash)] text-[var(--accent-text)] font-medium"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                          />
                        </svg>
                        Dashboard
                      </a>
                      {[
                        "Administration",
                        "Students",
                        "Examination",
                        "Time Tables",
                        "Fees & Wallets",
                        "Library",
                      ].map((item) => (
                        <a
                          key={item}
                          href="#"
                          className="flex items-center gap-1.5 px-2 py-1.5 rounded text-[var(--text-secondary)]"
                        >
                          {item}
                        </a>
                      ))}
                      <div className="pt-2 mt-2 border-t border-[var(--border-light)]">
                        <p className="px-2 text-[7px] text-[var(--text-muted)] italic mb-1">
                          More
                        </p>
                        {["Bulk SMS", "School Events"].map((item) => (
                          <a
                            key={item}
                            href="#"
                            className="flex items-center gap-1.5 px-2 py-1.5 rounded text-[var(--text-secondary)]"
                          >
                            {item}
                          </a>
                        ))}
                      </div>
                    </nav>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 p-3 bg-[var(--bg-section)]">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h2 className="text-sm font-bold text-[var(--text-primary)]">
                          Dashboard
                        </h2>
                        <p className="text-[9px] text-[var(--text-muted)]">
                          Welcome back, Admin
                        </p>
                      </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[
                        { l: "Total Students", v: "1,234", s: "+12%" },
                        { l: "Teachers", v: "48", s: "Active" },
                        { l: "Fee Collection", v: "89%", s: "This month" },
                        { l: "Attendance", v: "94%", s: "Today" },
                      ].map((s, i) => (
                        <div
                          key={i}
                          className="bg-[var(--bg-card)] rounded-lg p-2 border border-[var(--border-light)]"
                        >
                          <p className="text-[8px] text-[var(--text-muted)]">
                            {s.l}
                          </p>
                          <p className="text-lg font-bold text-[var(--text-primary)]">
                            {s.v}
                          </p>
                          <p className="text-[7px] text-[var(--accent-text)]">
                            {s.s}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {/* Fee Collection - Bar Chart */}
                      <div className="bg-[var(--bg-card)] rounded-lg p-2 border border-[var(--border-light)]">
                        <h3 className="text-[10px] font-semibold text-[var(--text-primary)] mb-2">
                          Fee Collection
                        </h3>
                        <div className="flex items-end gap-1 h-16">
                          {[60, 75, 65, 85, 80, 90].map((h, i) => (
                            <div
                              key={i}
                              className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t"
                              style={{ height: `${h}%` }}
                            />
                          ))}
                        </div>
                        <p className="text-[8px] text-blue-600 font-semibold mt-1">
                          KES 2.4M collected
                        </p>
                      </div>

                      {/* Library - Line Chart */}
                      <div className="bg-[var(--bg-card)] rounded-lg p-2 border border-[var(--border-light)]">
                        <h3 className="text-[10px] font-semibold text-[var(--text-primary)] mb-2">
                          Library Activity
                        </h3>
                        <svg className="w-full h-16" viewBox="0 0 100 40">
                          <polyline
                            fill="none"
                            stroke="url(#purpleGrad)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            points="5,30 20,22 35,26 50,15 65,18 80,8 95,12"
                          />
                          <defs>
                            <linearGradient
                              id="purpleGrad"
                              x1="0%"
                              y1="0%"
                              x2="100%"
                              y2="0%"
                            >
                              <stop offset="0%" stopColor="#a855f7" />
                              <stop offset="100%" stopColor="#7c3aed" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <p className="text-[8px] text-purple-600 font-semibold mt-1">
                          342 books this month
                        </p>
                      </div>

                      {/* Recent Activity */}
                      <div className="bg-[var(--bg-card)] rounded-lg p-2 border border-[var(--border-light)]">
                        <h3 className="text-[10px] font-semibold text-[var(--text-primary)] mb-2">
                          Recent Activity
                        </h3>
                        <div className="space-y-1.5">
                          {[
                            {
                              t: "New student enrolled",
                              c: "bg-green-100",
                              tc: "text-green-600",
                            },
                            {
                              t: "Fee payment received",
                              c: "bg-blue-100",
                              tc: "text-blue-600",
                            },
                            {
                              t: "SMS sent to parents",
                              c: "bg-orange-100",
                              tc: "text-orange-600",
                            },
                            {
                              t: "Report cards generated",
                              c: "bg-purple-100",
                              tc: "text-purple-600",
                            },
                          ].map((a, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <div
                                className={`w-4 h-4 rounded-full ${a.c} flex-shrink-0`}
                              ></div>
                              <p className="text-[8px] text-[var(--text-primary)] truncate">
                                {a.t}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
